import { useState } from 'react';
import { useSubscription } from './useSubscription';
import { subscriptionService } from '../services/subscriptionService';

/**
 * Gate a feature behind the subscription tier and surface the upgrade modal.
 *
 * NOTE: `checkFeatureAccess` currently dispatches on substrings of the feature
 * name, which is fragile — call sites have had to pick deliberately odd strings
 * to avoid matching the wrong branch. Replacing this with an explicit feature-key
 * enum is tracked as sc-276 / C4.
 */
export function useFeatureAccess() {
  const { isProUser, profile, subscribeToPro } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const checkFeatureAccess = (feature: string, showToast = true) => {
    // Pro users have unlimited access
    if (isProUser) {
      return true;
    }

    // Check specific feature limits for rookie users
    if (feature.toLowerCase().includes('practice')) {
      const hasReachedLimit = subscriptionService.hasReachedPracticeLimit(profile);
      if (hasReachedLimit && showToast) {
        setShowUpgradeModal(true);
      }
      return !hasReachedLimit;
    }

    if (feature.toLowerCase().includes('game plan')) {
      const hasReachedLimit = subscriptionService.hasReachedGamePlanLimit(profile);
      if (hasReachedLimit && showToast) {
        setShowUpgradeModal(true);
      }
      return !hasReachedLimit;
    }

    // Tennis live scoring pad — Rookie gets 1 match started per month, not a flat block.
    if (feature.toLowerCase().includes('live scoring')) {
      const hasReachedLimit = subscriptionService.hasReachedTennisScoringLimit(profile);
      if (hasReachedLimit && showToast) {
        setShowUpgradeModal(true);
      }
      return !hasReachedLimit;
    }

    // For other features, check if user is Pro
    if (!isProUser && showToast) {
      setShowUpgradeModal(true);
    }
    return isProUser;
  };

  const handleUpgrade = () => {
    subscribeToPro();
    setShowUpgradeModal(false);
  };

  const handleCloseModal = () => {
    setShowUpgradeModal(false);
  };

  return {
    checkFeatureAccess,
    isProUser,
    profile,
    showUpgradeModal,
    handleUpgrade,
    handleCloseModal
  };
}
